import { useMemo, useRef, useState } from 'react'

import { type FmcsaCargo, FmcsaCargoHints } from '@/components/freight/FmcsaCargoHints'
import { FreightEditorAudience } from '@/components/freight/freightEditorAudience'
import { FreightShareRow } from '@/components/freight/FreightShareRow'
import { Button } from '@/components/ui/button'
import { type SuggestionGroup, TagInput, TagInputAppearance } from '@/components/ui/tag-input'
import {
  addShare,
  evenShares,
  MAX_FREIGHT_TYPES,
  moveShare,
  normalizeShares,
  pasteShares,
  removeShare,
  type Share,
} from '@/lib/freightAllocation'
import { cn } from '@/lib/utils'
import { type CommodityItem } from '@/models/user'
import { type TagEntry } from '@/utils/tagText'

const PLACEHOLDER: Record<FreightEditorAudience, string> = {
  [FreightEditorAudience.STAFF]: 'Type what they haul and press Enter',
  [FreightEditorAudience.CUSTOMER]: 'Select commodities or type something...',
}

function overLimitMessage(excess: number): string {
  return `Remove ${excess} to continue, or summarize entries by typing in a simplified label. Remember "General freight" will get rejected by insurance providers.`
}

interface FreightAllocationEditorProps {
  id?: string
  value: CommodityItem[]
  onChange: (next: CommodityItem[]) => void
  suggestions: string[]
  suggestionGroups?: SuggestionGroup[]
  fmcsaCargo: FmcsaCargo[]
  audience?: FreightEditorAudience
  disabled?: boolean
}

interface Drag {
  index: number
  snapshot: Share[]
}

function sameShares(a: Share[], b: Share[]): boolean {
  return (
    a.length === b.length &&
    a.every((share, index) => share.name === b[index].name && share.percentage === b[index].percentage)
  )
}

/** Even within a whole percent, wherever the remainder sits: 34/33/33 counts as much as 33/33/34. */
function isEven(shares: Share[]): boolean {
  if (shares.length === 0) return true
  const percentages = shares.map((share) => share.percentage)
  return Math.max(...percentages) - Math.min(...percentages) <= 1
}

function describeMove(before: Share[], after: Share[], index: number): string {
  const moved = after[index]
  const others = after.filter(
    (share, position) => position !== index && share.percentage !== before[position].percentage
  )
  const summary = `${moved.name} ${moved.percentage}%.`
  if (others.length === 0) return summary
  return `${summary} Also moved: ${others.map((share) => `${share.name} ${share.percentage}%`).join(', ')}.`
}

/** Freight types as tags, each with a share slider; shares total 100 and stay even until someone sets one. */
export function FreightAllocationEditor({
  id,
  value,
  onChange,
  suggestions,
  suggestionGroups,
  fmcsaCargo,
  audience = FreightEditorAudience.STAFF,
  disabled,
}: FreightAllocationEditorProps) {
  const loaded = useMemo(() => normalizeShares(value), [value])
  const unsplittable = useMemo(() => fmcsaCargo.map((cargo) => cargo.name), [fmcsaCargo])
  const [draft, setDraft] = useState<Share[] | null>(null)
  const [announcement, setAnnouncement] = useState('')
  // A saved uneven split was set by hand; anything else re-splits evenly as types come and go.
  const [setByHand, setSetByHand] = useState(() => !isEven(loaded))
  const [emitted, setEmitted] = useState<Share[] | null>(null)
  const [seen, setSeen] = useState(loaded)
  // Values the parent restored (a discard, a reload) set the mode again; our own edits coming back don't.
  if (loaded !== seen) {
    setSeen(loaded)
    if (!emitted || !sameShares(loaded, emitted)) setSetByHand(!isEven(loaded))
  }
  const drag = useRef<Drag | null>(null)
  const shares = draft ?? loaded
  const names = shares.map((share) => share.name)
  const splitEvenlyHidden = shares.length < 2 || isEven(shares)
  const hasZeroShare = shares.some((share) => share.percentage === 0)

  function emit(next: Share[]) {
    setEmitted(next)
    onChange(next.map((share) => ({ name: share.name, percentage: share.percentage })))
  }

  function emitList(next: Share[]) {
    emit(setByHand ? next : evenShares(next))
  }

  function commitMove(before: Share[], after: Share[], index: number) {
    if (sameShares(before, after)) {
      setAnnouncement(`${after[index].name} ${after[index].percentage}%.`)
      return
    }
    setSetByHand(true)
    emit(after)
    setAnnouncement(describeMove(before, after, index))
  }

  function handleTagsChange(nextNames: string[]) {
    const keep = new Set(nextNames)
    let next = shares
    for (let index = next.length - 1; index >= 0; index -= 1) {
      if (!keep.has(next[index].name)) next = removeShare(next, index)
    }
    emitList(nextNames.reduce((list, name) => addShare(list, name), next))
  }

  function handlePastedShares(entries: TagEntry[]) {
    setSetByHand(true)
    emit(pasteShares(shares, entries))
  }

  function startDrag(index: number) {
    if (disabled || shares.length < 2) return
    drag.current = { index, snapshot: shares }
  }

  function endDrag() {
    const current = drag.current
    if (!current) return
    drag.current = null
    setDraft(null)
    if (draft) commitMove(current.snapshot, draft, current.index)
  }

  function handleSlide(index: number, next: number) {
    const current = drag.current
    if (current?.index === index) {
      setDraft(moveShare(current.snapshot, index, next))
      return
    }
    commitMove(shares, moveShare(shares, index, next), index)
  }

  return (
    <div className="flex flex-col gap-4">
      <TagInput
        id={id}
        value={names}
        onChange={handleTagsChange}
        onSharesFromPaste={handlePastedShares}
        suggestions={suggestions}
        suggestionGroups={suggestionGroups}
        unsplittable={unsplittable}
        maxTags={MAX_FREIGHT_TYPES}
        overLimitMessage={overLimitMessage}
        placeholder={PLACEHOLDER[audience]}
        appearance={
          audience === FreightEditorAudience.CUSTOMER ? TagInputAppearance.INPUT : TagInputAppearance.COMBOBOX
        }
        disabled={disabled}
        aria-label="Freight types"
      />
      <FmcsaCargoHints
        cargo={fmcsaCargo}
        added={names}
        audience={audience}
        disabled={disabled}
        onAdd={(name) => emitList(addShare(shares, name))}
      />
      {shares.length > 0 && (
        <ul aria-label="Freight shares" className="@container flex flex-col gap-1">
          {shares.map((share, index) => (
            <FreightShareRow
              key={share.name}
              share={share}
              disabled={disabled}
              fixed={shares.length === 1}
              onSlideStart={() => startDrag(index)}
              onSlide={(next) => handleSlide(index, next)}
              onSlideEnd={endDrag}
              onPercentCommit={(next) => commitMove(shares, moveShare(shares, index, next), index)}
              onRemove={() => emitList(removeShare(shares, index))}
            />
          ))}
        </ul>
      )}
      {shares.length > 0 && (
        // Kept in the layout while hidden, so the rows above don't jump when it appears.
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <p className="text-muted-foreground col-start-2 text-center text-xs">
            {hasZeroShare && 'Items at 0% will be removed when you save or continue.'}
          </p>
          <Button
            type="button"
            variant="link"
            size="sm"
            className={cn('h-auto justify-self-end px-0', splitEvenlyHidden && 'invisible')}
            aria-hidden={splitEvenlyHidden || undefined}
            tabIndex={splitEvenlyHidden ? -1 : undefined}
            disabled={disabled}
            onClick={() => {
              const even = evenShares(shares)
              setSetByHand(false)
              emit(even)
              setAnnouncement(
                `Split evenly: ${even.map((share) => `${share.name} ${share.percentage}%`).join(', ')}.`
              )
            }}
          >
            Split evenly
          </Button>
        </div>
      )}
      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </div>
  )
}
